import PT from 'prop-types';
import React from 'react';

import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { EsignatureType } from '~/enums/EsignatureType';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import Chip from '~/ui/components/Chip/Chip';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import styles from '~/ui/styles';

function StatusAndActions({
  disabled,
  esignatureIntegration,
  isActive = false,
  isLoading = false,
  isPending = false,
  onConnect,
  onDelete,
  onToggleCollapse,
}) {
  const intl = useIntl();

  // TODO: abstract into reusable UI component
  const IconLoading = (
    <Box
      sx={{
        animation: `${styles.keyframes.spin} 1s linear infinite`,
      }}
    >
      <Icon icon="loading" />
    </Box>
  );

  const { id, error, isDefault } = esignatureIntegration;
  const items = [];

  if (disabled) {
    items.push(
      <Button
        disabled={disabled}
        text={intl.formatMessage({
          id: 'StatusAndActions.legacy.connect1',
          defaultMessage: 'Connect',
          description: 'Connect button text',
        })}
        variant="action"
      />,
    );
  } else {
    // default status
    if (isDefault) {
      items.push(
        <Chip
          key="default"
          text={intl.formatMessage({
            id: 'StatusAndActions..egacy.default',
            defaultMessage: 'Default',
            description: 'Default status chip',
          })}
          variant="info"
        />,
      );
    }

    // esignatureIntegration status
    if (!isActive && !id) {
      items.push(
        <Button
          disabled={disabled}
          text={intl.formatMessage({
            id: 'StatusAndActions.legacy.connect2',
            defaultMessage: 'Connect',
            description: 'Connect button text',
          })}
          onClick={onConnect}
          variant="action"
        />,
      );
    } else if (error) {
      items.push(
        <Tooltip key="error" tooltip={error.message}>
          <Chip
            text={intl.formatMessage({
              id: 'StatusAndActions.legacy.validationError',
              defaultMessage: 'Validation error',
              description: 'Validation error chip text',
            })}
            variant="danger"
          />
        </Tooltip>,
      );
    } else if (isActive && isPending) {
      items.push(
        <Chip
          key="pending"
          text={intl.formatMessage({
            id: 'StatusAndActions.legacy.pending',
            defaultMessage: 'Pending',
            description: 'Pending status chip',
          })}
          variant="warning"
        />,
      );
    } else if (isActive && !id) {
      items.push(
        <Chip
          key="not-connected"
          text={intl.formatMessage({
            id: 'StatusAndActions.legacy.notConnected',
            defaultMessage: 'Not connected',
            description: 'Not connected status chip',
          })}
          variant="info"
        />,
      );
    } else if (id) {
      items.push(
        <Chip
          key="connected"
          text={intl.formatMessage({
            id: 'StatusAndActions.legacy.connected',
            defaultMessage: 'Connected',
            description: 'Connected status chip',
          })}
          variant="success"
        />,
      );
    }

    // delete status
    if (id) {
      items.push(
        <Box key="delete" ml={6} pl={8} sx={{ borderLeft: 'border' }}>
          {isLoading ? (
            IconLoading
          ) : (
            <IconButton
              disabled={isLoading}
              icon="trash"
              label={intl.formatMessage({
                id: 'StatusAndActions.remove',
                defaultMessage: 'remove',
                description: 'Remove button label',
              })}
              onClick={onDelete}
            />
          )}
        </Box>,
      );
    }
  }

  return (
    <FlexLayout alignItems="center" space={2} onClick={onToggleCollapse}>
      {items}
    </FlexLayout>
  );
}

StatusAndActions.propTypes = {
  esignatureIntegration: PT.shape({
    id: PT.string,
    config: PT.object.isRequired,
    error: PT.shape({ message: PT.string.isRequired }),
    isDefault: PT.bool.isRequired,
    isUserLoggedIn: PT.bool,
    shouldUseCompanyAccount: PT.bool,
    type: PT.oneOf(Object.values(EsignatureType)),
  }).isRequired,
  isActive: PT.bool,
  isLoading: PT.bool,
  isPending: PT.bool,
  onConnect: PT.func.isRequired,
  onDelete: PT.func.isRequired,
  onToggleCollapse: PT.func.isRequired,
};

export default StatusAndActions;
